export function normaliseName(s) {
    return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}
/**
 * Rebuild roster-to-runtime bindings at a main-session checkpoint
 * (spec section 8). Rules:
 *  - persistent identifier equal to a previous binding's  -> auto rebind
 *  - otherwise a ranked candidate requiring operator confirmation
 *  - two roster entries sharing a display name are never auto-bound and
 *    their candidates are marked as collisions
 */
export function rebind(input) {
    const { roster, live, previous, lastRoom, now } = input;
    const auto = [];
    const candidates = [];
    const claimedLive = new Set();
    const boundRoster = new Set();
    const prevByPersistent = new Map();
    for (const b of previous)
        if (b.persistentId)
            prevByPersistent.set(b.persistentId, b);
    for (const lp of live) {
        if (!lp.persistentId)
            continue;
        const prev = prevByPersistent.get(lp.persistentId);
        if (!prev || boundRoster.has(prev.participantId))
            continue;
        auto.push({
            participantId: prev.participantId,
            runtimeId: lp.runtimeId,
            persistentId: lp.persistentId,
            boundAt: now().toISOString(),
            boundBy: 'auto_persistent',
            stale: false,
        });
        claimedLive.add(lp.runtimeId);
        boundRoster.add(prev.participantId);
    }
    const nameCounts = new Map();
    for (const p of roster) {
        const n = normaliseName(p.displayName);
        nameCounts.set(n, (nameCounts.get(n) ?? 0) + 1);
    }
    const liveNameCounts = new Map();
    for (const lp of live) {
        const n = normaliseName(lp.displayName);
        liveNameCounts.set(n, (liveNameCounts.get(n) ?? 0) + 1);
    }
    const prevByParticipant = new Map(previous.map((b) => [b.participantId, b]));
    for (const p of roster) {
        if (boundRoster.has(p.participantId))
            continue;
        const pname = normaliseName(p.displayName);
        const collision = (nameCounts.get(pname) ?? 0) > 1;
        for (const lp of live) {
            if (claimedLive.has(lp.runtimeId))
                continue;
            const reasons = [];
            let score = 0;
            const lname = normaliseName(lp.displayName);
            if (lname === pname) {
                score += 10;
                reasons.push('display name matches');
                if ((liveNameCounts.get(lname) ?? 0) > 1)
                    reasons.push('several attendees share this name');
            }
            else if (pname && lname && (lname.includes(pname) || pname.includes(lname))) {
                score += 4;
                reasons.push('display name partially matches');
            }
            if (p.email && lp.email && p.email.toLowerCase() === lp.email.toLowerCase()) {
                score += 20;
                reasons.push('email matches');
            }
            const prev = prevByParticipant.get(p.participantId);
            if (prev && prev.runtimeId === lp.runtimeId) {
                score += 6;
                reasons.push('same runtime identifier as before');
            }
            const room = lastRoom.get(p.participantId);
            if (room && lp.location && lp.location !== 'main' && lp.location.roomId === room) {
                score += 3;
                reasons.push('seen in the room they were last assigned to');
            }
            if (score === 0)
                continue;
            if (collision)
                reasons.push('roster has more than one person with this name');
            candidates.push({ participantId: p.participantId, runtimeId: lp.runtimeId, score, reasons, requiresConfirmation: true });
        }
    }
    candidates.sort((a, b) => b.score - a.score || (a.participantId < b.participantId ? -1 : 1));
    const withCandidate = new Set(candidates.map((c) => c.participantId));
    const unmatchedRoster = roster.map((p) => p.participantId).filter((id) => !boundRoster.has(id) && !withCandidate.has(id));
    const claimedOrSuggested = new Set([...claimedLive, ...candidates.map((c) => c.runtimeId)]);
    const unmatchedLive = live.map((l) => l.runtimeId).filter((id) => !claimedOrSuggested.has(id));
    return { auto, candidates, unmatchedRoster, unmatchedLive };
}
export function confirmBinding(candidate, live, now) {
    const b = {
        participantId: candidate.participantId,
        runtimeId: candidate.runtimeId,
        boundAt: now().toISOString(),
        boundBy: 'operator',
        stale: false,
    };
    if (live.persistentId)
        b.persistentId = live.persistentId;
    return b;
}
//# sourceMappingURL=identity.js.map